<template>
    <div class="flex flex-column align-items-center w-full mb-8">
        <div class="px-3 lg:px-8 py-5 w-full bg-white shadow-1 border-round">
            <div class="flex flex-column md:flex-row w-full">

                <!-- Features List - Desktop -->
                <div class="hidden md:flex flex-column justify-content-end flex-1">
                    <div v-for="(def, index) in featureDefinitions" :key="def.key"
                        :class="['feature-row justify-content-start border-bottom-1 border-gray-200 font-normal text-500 pl-2 text-sm', { 'border-top-1': index === 0 }]">
                        {{ def.label }}
                        <i v-if="def.info" class="pi pi-info-circle text-xs ml-1" :title="def.info"></i>
                    </div>
                </div>

                <div v-for="(pkg, index) in packages" :key="pkg.id + index"
                    class="flex flex-column align-items-center text-center border-left-none md:border-left-1 border-gray-200 flex-1 mb-6 md:mb-0">

                    <div class="pkg-header-title w-full mt-4 px-2 md:px-4">
                        <h3 class="text-lg lg:text-base xl:text-lg font-medium text-800 m-0">{{ pkg.name }}</h3>
                    </div>

                    <div class="pkg-header-price w-full mt-4 px-2 md:px-4">
                        <span class="text-5xl lg:text-4xl xl:text-5xl font-bold H6-Medium">
                            <template v-if="isNumeric(pkg.priceLabel)">
                                <span class="text-xl font-medium mr-1 text-800">Rs.</span>{{ pkg.priceLabel }}
                            </template>
                            <template v-else>
                                <span class="text-4xl xl:text-5xl font-bold H6-Medium">
                                {{ pkg.priceLabel }}
                                </span>
                            </template>
                        </span>
                    </div>

                    <p class="text-600 text-xs mb-6">per month/ user</p>

                    <div class="pkg-header-button w-full px-2 md:px-4">
                        <Button
                            :label="getButtonLabel(pkg.id)"
                            :class="getButtonClass(pkg.id)"
                            :disabled="isButtonDisabled(pkg.id)"
                            @click="selectPackage(pkg, getButtonAction(pkg.id))"
                        />
                    </div>
                    <p v-if="getButtonAction(pkg.id) === 'locked'" class="text-xs text-500 mt-2 mb-0 px-2 text-center">
                        Change plan after completing onboarding
                    </p>

                    <div class="pkg-header-extras my-6 w-full text-left px-2 md:px-4">
                        <p v-if="pkg.subtitle" class="text-sm text-500 font-normal text-left m-0">{{ pkg.subtitle }}</p>
                        <div v-if="pkg.priceBreakdown" class="text-xs text-500 font-normal text-left">
                            <div v-for="item in pkg.priceBreakdown" :key="item.label"
                                class="flex justify-content-between py-1">
                                <span>{{ item.label }}</span>
                                <span>{{ isNumeric(item.price) ? 'Rs. ' + item.price + '.00' : item.price }}</span>
                            </div>
                        </div>
                    </div>

                    <!-- Features - Desktop -->
                    <div class="w-full hidden md:block mt-auto">
                        <div v-for="(def, index) in featureDefinitions" :key="def.key"
                            :class="['feature-row border-bottom-1 border-gray-200 justify-content-center w-full', { 'border-top-1': index === 0 }]">
                            <span v-if="typeof pkg.features[def.key] === 'string' || typeof pkg.features[def.key] === 'number'"
                                class="text-sm text-600 font-normal">
                                {{ pkg.features[def.key] }}
                            </span>
                            <template v-else>
                                <i class="pi pi-check text-600 text-sm" v-if="pkg.features[def.key] === true"></i>
                                <i class="pi pi-times text-600 text-sm" v-else></i>
                            </template>
                        </div>
                    </div>

                    <!-- Features - Mobile -->
                    <div class="w-full md:hidden mt-4 border-round">
                        <div v-for="def in featureDefinitions" :key="def.key"
                            class="flex justify-content-between align-items-center py-3 px-3 border-bottom-1 border-gray-200">
                            <span class="text-sm text-500 font-normal text-left">{{ def.label }}</span>
                            <span>
                                <span v-if="typeof pkg.features[def.key] === 'string' || typeof pkg.features[def.key] === 'number'"
                                    class="text-sm text-600 font-normal">
                                    {{ pkg.features[def.key] }}
                                </span>
                                <template v-else>
                                    <i class="pi pi-check text-600 text-sm" v-if="pkg.features[def.key] === true"></i>
                                    <i class="pi pi-times text-600 text-sm" v-else></i>
                                </template>
                            </span>
                        </div>
                    </div>
                </div>

            </div>
        </div>        

        <!-- Disclaimer -->
        <div class="text-center w-full max-w-4xl pt-6 pb-2">
            <h3 class="disclaimer-heading text-lg font-semibold mb-2" style="color: #dc2626;">Disclaimer</h3>
            <p class="text-700 text-sm m-0 mb-2">
                All payments must be processed through the platform. ILearn'd Tutors is not responsible for issues caused by third-party payment providers or user error.
            </p>
            <p class="text-700 text-sm m-0">
                Feature availability may vary depending on your subscription plan and development stage.
            </p>
        </div>

    </div>
</template>

<script setup>
import { defineProps, defineEmits } from 'vue';
import Button from 'primevue/button';

const props = defineProps({
    mode: {
        type: String,
        default: 'onboarding'
    },
    currentPlanId: {
        type: String,
        default: null
    },
    featureDefinitions: {
        type: Array,
        required: true
    },
    packages: {
        type: Array,
        required: true
    },
    disableActions: {
        type: Boolean,
        default: false
    }
});

const emit = defineEmits(['package-action']);

const isNumeric = (label) => {
    if (!label) return false;
    const cleanValue = label.toString().replace(/Rs\.?\s?|,/g, '').trim();
    return cleanValue !== '' && !isNaN(cleanValue);
};

const getButtonAction = (pkgId) => {
    if (props.mode === 'onboarding') {
        if (!props.currentPlanId) return 'start';
        return pkgId === props.currentPlanId ? 'current' : 'locked';
    }
    if (pkgId === props.currentPlanId) return 'current';
    // Use array index (packages sorted by displayOrder from API) for upgrade/downgrade logic
    const currentIdx = props.packages.findIndex(p => p.id === props.currentPlanId);
    const targetIdx  = props.packages.findIndex(p => p.id === pkgId);
    return targetIdx > currentIdx ? 'upgrade' : 'downgrade';
};

const getButtonLabel = (pkgId) => {
    const pkg = props.packages.find(p => p.id === pkgId);
    if (pkg?.actionLabel) return pkg.actionLabel;

    const action = getButtonAction(pkgId);
    switch (action) {
        case 'start':    return 'Get Started';
        case 'current':  return props.mode === 'onboarding' ? 'Plan Selected' : 'Your current plan';
        case 'locked':   return 'Get Started';
        case 'upgrade':  return 'Upgrade';
        case 'downgrade':return 'Downgrade';
        default:         return 'Select';
    }
};

const getButtonClass = (pkgId) => {
    const action = getButtonAction(pkgId);
    if (action === 'current' && props.mode === 'onboarding')
        return 'p-button-danger w-full opacity-80 cursor-default';
    if (action === 'current')
        return 'p-button-outlined p-button-secondary w-full opacity-60';
    if (action === 'locked')
        return 'p-button-outlined p-button-secondary w-full opacity-40';
    if (pkgId === 'foundation' || action === 'downgrade')
        return 'p-button-outlined p-button-secondary w-full text-700';
    return 'p-button-danger w-full';
};

const isButtonDisabled = (pkgId) => {
    if (props.disableActions) return true;

    const pkg = props.packages.find(p => p.id === pkgId);
    if (pkg && typeof pkg.disabled === 'boolean') {
        return pkg.disabled;
    }

    const action = getButtonAction(pkgId);
    return action === 'current' || action === 'locked';
};

const selectPackage = (pkg, action) => {
    emit('package-action', { package: pkg, action: action });
};
</script>

<style scoped>

.feature-row {
    min-height: 55px;
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 8px 4px;
    word-break: break-word;
}

.text-500 {
    color: #7c858d !important;
}

.pkg-header-title {
    height: 48px;
    display: flex;
    align-items: center;
    justify-content: center;
}

.pkg-header-price {
    height: 100px;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
}

.pkg-header-button {
    height: 45px;
    display: flex;
    align-items: center;
    justify-content: center;
}

.pkg-header-button :deep(.p-button) {
    font-size: 1rem;
    border-radius: 6px;
}

.pkg-header-extras {
    min-height: 140px;
}

.p-button-danger {
    background-color: var(--orange-500) !important;
    border: 1px solid var(--orange-500) !important;
    color: white !important;
    font-weight: 600;
}

.p-button-danger:hover {
    background-color: var(--orange-600) !important;
    border-color: var(--orange-600) !important;
}

.p-button-outlined {
    background-color: var(--accent-50) !important;
    color: black !important;
    font-weight: 600;
}

.p-button-outlined:not(:disabled):hover {
    background-color: var(--accent-100) !important;
}

</style>
